import React, { useState, useEffect } from "react";
import {
  FaGlobe,
  FaSearch,
  FaShare,
  FaEnvelope,
  FaExternalLinkAlt,
  FaChartPie,
  FaUsers,
  FaMousePointer,
  FaClock,
  FaSync,
  FaFilter,
  FaDownload,
  FaArrowUp,
  FaArrowDown,
  FaLink,
  FaCopy,
  FaPlus,
  FaTools,
  FaTiktok,
  FaInstagram,
  FaFacebook,
  FaWhatsapp,
  FaLinkedin,
  FaTwitter,
  FaYoutube,
  FaTelegram,
  FaGoogle,
} from "react-icons/fa";
import { getTrafficSourceStats } from "../firebase";
import "./TrafficSources.css";

const TrafficSources = () => {
  const [trafficData, setTrafficData] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [sortBy, setSortBy] = useState("sessions");
  const [sortOrder, setSortOrder] = useState("desc");

  // Link Builder States
  const [showLinkBuilder, setShowLinkBuilder] = useState(false);
  const [linkForm, setLinkForm] = useState({
    source: "",
    medium: "social",
    campaign: "",
    term: "",
    content: "",
    useUTM: true,
  });
  const [generatedLink, setGeneratedLink] = useState("");

  useEffect(() => {
    loadTrafficData();
  }, [dateRange]);

  const loadTrafficData = async () => {
    setLoading(true);
    try {
      const data = await getTrafficSourceStats(dateRange);
      setTrafficData(data);
    } catch (error) {
      console.error("Error loading traffic data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (source) => {
    switch (source.toLowerCase()) {
      case "tiktok":
        return <FaTiktok />;
      case "instagram":
        return <FaInstagram />;
      case "facebook":
        return <FaFacebook />;
      case "whatsapp":
        return <FaWhatsapp />;
      case "linkedin":
        return <FaLinkedin />;
      case "twitter":
      case "x":
        return <FaTwitter />;
      case "youtube":
        return <FaYoutube />;
      case "telegram":
        return <FaTelegram />;
      case "google":
        return <FaGoogle />;
      case "direct":
        return <FaGlobe />;
      default:
        return <FaExternalLinkAlt />;
    }
  };

  const getSourceColor = (source) => {
    switch (source.toLowerCase()) {
      case "tiktok":
        return "#ff0050";
      case "instagram":
        return "#E4405F";
      case "facebook":
        return "#1877F2";
      case "whatsapp":
        return "#25D366";
      case "linkedin":
        return "#0A66C2";
      case "twitter":
      case "x":
        return "#1DA1F2";
      case "youtube":
        return "#FF0000";
      case "telegram":
        return "#0088CC";
      case "google":
        return "#4285F4";
      case "direct":
        return "#6c757d";
      default:
        return "#007bff";
    }
  };

  const getMediumBadgeClass = (medium) => {
    switch (medium.toLowerCase()) {
      case "social":
        return "medium-social";
      case "messaging":
        return "medium-messaging";
      case "organic":
        return "medium-organic";
      case "cpc":
        return "medium-cpc";
      case "email":
        return "medium-email";
      case "referral":
        return "medium-referral";
      case "educational":
        return "medium-educational";
      default:
        return "medium-other";
    }
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds) return "0s";
    const seconds = Math.round(milliseconds / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const calculateConversionRate = (conversions, sessions) => {
    if (sessions === 0) return 0;
    return Math.round((conversions / sessions) * 100);
  };

  const sortedTrafficData = Object.entries(trafficData).sort((a, b) => {
    const [, dataA] = a;
    const [, dataB] = b;

    let valueA, valueB;

    switch (sortBy) {
      case "sessions":
        valueA = dataA.sessions;
        valueB = dataB.sessions;
        break;
      case "conversions":
        valueA = dataA.conversions;
        valueB = dataB.conversions;
        break;
      case "conversionRate":
        valueA = calculateConversionRate(dataA.conversions, dataA.sessions);
        valueB = calculateConversionRate(dataB.conversions, dataB.sessions);
        break;
      case "avgDuration":
        valueA = dataA.sessions > 0 ? dataA.totalDuration / dataA.sessions : 0;
        valueB = dataB.sessions > 0 ? dataB.totalDuration / dataB.sessions : 0;
        break;
      default:
        valueA = dataA.sessions;
        valueB = dataB.sessions;
    }

    if (sortOrder === "asc") {
      return valueA - valueB;
    } else {
      return valueB - valueA;
    }
  });

  const totalSessions = Object.values(trafficData).reduce(
    (sum, data) => sum + data.sessions,
    0
  );
  const totalConversions = Object.values(trafficData).reduce(
    (sum, data) => sum + data.conversions,
    0
  );

  const exportData = () => {
    const exportObj = {
      exportDate: new Date().toISOString(),
      dateRange: `${dateRange} days`,
      totalSessions,
      totalConversions,
      overallConversionRate: calculateConversionRate(
        totalConversions,
        totalSessions
      ),
      trafficSources: sortedTrafficData.map(([key, data]) => ({
        source: data.source,
        medium: data.medium,
        campaign: data.campaign,
        sessions: data.sessions,
        conversions: data.conversions,
        conversionRate: calculateConversionRate(
          data.conversions,
          data.sessions
        ),
        avgSessionDuration: formatDuration(
          data.sessions > 0 ? data.totalDuration / data.sessions : 0
        ),
      })),
    };

    const dataStr = JSON.stringify(exportObj, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const exportFileDefaultName = `traffic_sources_${
      new Date().toISOString().split("T")[0]
    }.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  // Link Builder Functions
  const generateTrackingLink = () => {
    const baseUrl = "https://www.almanyakampus.com/";
    let url = new URL(baseUrl);

    if (linkForm.useUTM) {
      // UTM Parameters
      if (linkForm.source) url.searchParams.set("utm_source", linkForm.source);
      if (linkForm.medium) url.searchParams.set("utm_medium", linkForm.medium);
      if (linkForm.campaign)
        url.searchParams.set("utm_campaign", linkForm.campaign);
      if (linkForm.term) url.searchParams.set("utm_term", linkForm.term);
      if (linkForm.content)
        url.searchParams.set("utm_content", linkForm.content);
    } else {
      // Simple ref parameter
      if (linkForm.source) url.searchParams.set("ref", linkForm.source);
    }

    setGeneratedLink(url.toString());
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      // Success feedback - you could add a toast here
      alert("Link kopyalandı!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = generatedLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      alert("Link kopyalandı!");
    }
  };

  const resetLinkForm = () => {
    setLinkForm({
      source: "",
      medium: "social",
      campaign: "",
      term: "",
      content: "",
      useUTM: true,
    });
    setGeneratedLink("");
  };

  const getQuickSourceOptions = () => [
    { value: "tiktok", label: "TikTok", medium: "social" },
    { value: "instagram", label: "Instagram", medium: "social" },
    { value: "facebook", label: "Facebook", medium: "social" },
    { value: "whatsapp", label: "WhatsApp", medium: "messaging" },
    { value: "linkedin", label: "LinkedIn", medium: "social" },
    { value: "twitter", label: "Twitter/X", medium: "social" },
    { value: "youtube", label: "YouTube", medium: "social" },
    { value: "telegram", label: "Telegram", medium: "messaging" },
    { value: "google", label: "Google Ads", medium: "cpc" },
    { value: "email", label: "Email", medium: "email" },
  ];

  const setQuickSource = (source, medium) => {
    setLinkForm((prev) => ({
      ...prev,
      source,
      medium,
    }));
  };

  if (loading) {
    return (
      <div className="traffic-sources-container">
        <div className="traffic-header">
          <h2>
            <FaChartPie /> Trafik Kaynakları
          </h2>
          <p>Marketing kampanya performans analizi</p>
        </div>
        <div className="traffic-loading">
          <div className="loading-spinner"></div>
          <p>Trafik verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="traffic-sources-container">
      <div className="traffic-header">
        <div className="header-content">
          <h2>
            <FaChartPie /> Trafik Kaynakları
          </h2>
          <p>Marketing kampanya performans analizi</p>
        </div>
        <div className="header-actions">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="date-range-select"
          >
            <option value={7}>Son 7 gün</option>
            <option value={30}>Son 30 gün</option>
            <option value={90}>Son 90 gün</option>
          </select>
          <button
            className="action-btn link-builder-btn"
            onClick={() => setShowLinkBuilder(!showLinkBuilder)}
            title="Link Oluşturucu"
          >
            <FaTools /> Link Oluştur
          </button>
          <button
            className="action-btn refresh-btn"
            onClick={loadTrafficData}
            title="Verileri Yenile"
          >
            <FaSync /> Yenile
          </button>
          <button
            className="action-btn export-btn"
            onClick={exportData}
            title="Verileri Dışa Aktar"
          >
            <FaDownload /> Dışa Aktar
          </button>
        </div>
      </div>

      {sortedTrafficData.length === 0 ? (
        <div className="no-data-container">
          <div className="no-data-content">
            <FaChartPie className="no-data-icon" />
            <h3>Henüz Trafik Verisi Yok</h3>
            <p>
              Kullanıcılar farklı kaynaklardan siteyi ziyaret etmeye
              başladığında veriler burada görünecek.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="traffic-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <FaUsers />
              </div>
              <div className="summary-content">
                <div className="summary-number">{totalSessions}</div>
                <div className="summary-label">Toplam Oturum</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">
                <FaMousePointer />
              </div>
              <div className="summary-content">
                <div className="summary-number">{totalConversions}</div>
                <div className="summary-label">Toplam Dönüşüm</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">
                <FaChartPie />
              </div>
              <div className="summary-content">
                <div className="summary-number">
                  {calculateConversionRate(totalConversions, totalSessions)}%
                </div>
                <div className="summary-label">Genel Dönüşüm Oranı</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">
                <FaExternalLinkAlt />
              </div>
              <div className="summary-content">
                <div className="summary-number">{sortedTrafficData.length}</div>
                <div className="summary-label">Farklı Kaynak</div>
              </div>
            </div>
          </div>

          {/* Link Builder Tool */}
          {showLinkBuilder && (
            <div className="link-builder-container">
              <div className="link-builder-header">
                <h3>
                  <FaLink /> Tracking Link Oluşturucu
                </h3>
                <p>
                  Marketing kampanyalarınız için özel tracking linkler oluşturun
                </p>
              </div>

              <div className="link-builder-content">
                <div className="quick-sources">
                  <h4>Hızlı Kaynaklar:</h4>
                  <div className="quick-source-buttons">
                    {getQuickSourceOptions().map((option) => (
                      <button
                        key={option.value}
                        className="quick-source-btn"
                        onClick={() =>
                          setQuickSource(option.value, option.medium)
                        }
                      >
                        {getSourceIcon(option.value)} {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="link-form">
                  <div className="form-section">
                    <div className="form-toggle">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={linkForm.useUTM}
                          onChange={(e) =>
                            setLinkForm((prev) => ({
                              ...prev,
                              useUTM: e.target.checked,
                            }))
                          }
                        />
                        <span className="toggle-text">
                          {linkForm.useUTM
                            ? "UTM Parametreleri Kullan"
                            : "Basit REF Parametresi Kullan"}
                        </span>
                      </label>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          Kaynak {linkForm.useUTM ? "(utm_source)" : "(ref)"} *
                        </label>
                        <input
                          type="text"
                          value={linkForm.source}
                          onChange={(e) =>
                            setLinkForm((prev) => ({
                              ...prev,
                              source: e.target.value,
                            }))
                          }
                          placeholder={
                            linkForm.useUTM
                              ? "örn: tiktok, instagram"
                              : "örn: tiktok_bio"
                          }
                          className="form-input"
                        />
                      </div>

                      {linkForm.useUTM && (
                        <div className="form-group">
                          <label>Medium (utm_medium)</label>
                          <select
                            value={linkForm.medium}
                            onChange={(e) =>
                              setLinkForm((prev) => ({
                                ...prev,
                                medium: e.target.value,
                              }))
                            }
                            className="form-input"
                          >
                            <option value="social">Social</option>
                            <option value="messaging">Messaging</option>
                            <option value="email">Email</option>
                            <option value="cpc">CPC (Ads)</option>
                            <option value="referral">Referral</option>
                            <option value="organic">Organic</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {linkForm.useUTM && (
                      <>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Kampanya (utm_campaign)</label>
                            <input
                              type="text"
                              value={linkForm.campaign}
                              onChange={(e) =>
                                setLinkForm((prev) => ({
                                  ...prev,
                                  campaign: e.target.value,
                                }))
                              }
                              placeholder="örn: ocak_promosyonu, yaz_kampanyasi"
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label>Anahtar Kelime (utm_term)</label>
                            <input
                              type="text"
                              value={linkForm.term}
                              onChange={(e) =>
                                setLinkForm((prev) => ({
                                  ...prev,
                                  term: e.target.value,
                                }))
                              }
                              placeholder="örn: almanya, universite"
                              className="form-input"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>İçerik (utm_content)</label>
                            <input
                              type="text"
                              value={linkForm.content}
                              onChange={(e) =>
                                setLinkForm((prev) => ({
                                  ...prev,
                                  content: e.target.value,
                                }))
                              }
                              placeholder="örn: story, post, bio_link"
                              className="form-input"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="form-actions">
                      <button
                        className="generate-btn"
                        onClick={generateTrackingLink}
                        disabled={!linkForm.source}
                      >
                        <FaLink /> Link Oluştur
                      </button>
                      <button className="reset-btn" onClick={resetLinkForm}>
                        <FaSync /> Sıfırla
                      </button>
                    </div>
                  </div>

                  {generatedLink && (
                    <div className="generated-link-section">
                      <h4>Oluşturulan Link:</h4>
                      <div className="generated-link">
                        <input
                          type="text"
                          value={generatedLink}
                          readOnly
                          className="link-input"
                        />
                        <button className="copy-btn" onClick={copyToClipboard}>
                          <FaCopy /> Kopyala
                        </button>
                      </div>
                      <div className="link-preview">
                        <small>
                          Bu link ile gelen trafik{" "}
                          <strong>{linkForm.source}</strong> kaynağı olarak
                          track edilecek
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="traffic-controls">
            <div className="sort-controls">
              <label>Sırala:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="sessions">Oturum Sayısı</option>
                <option value="conversions">Dönüşüm Sayısı</option>
                <option value="conversionRate">Dönüşüm Oranı</option>
                <option value="avgDuration">Ortalama Süre</option>
              </select>
              <button
                className={`sort-order-btn ${sortOrder}`}
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
              >
                {sortOrder === "asc" ? <FaArrowUp /> : <FaArrowDown />}
              </button>
            </div>
          </div>

          <div className="traffic-sources-grid">
            {sortedTrafficData.map(([key, data]) => {
              const conversionRate = calculateConversionRate(
                data.conversions,
                data.sessions
              );
              const avgDuration =
                data.sessions > 0 ? data.totalDuration / data.sessions : 0;
              const sessionPercentage = Math.round(
                (data.sessions / totalSessions) * 100
              );

              return (
                <div key={key} className="traffic-source-card">
                  <div className="source-header">
                    <div
                      className="source-icon"
                      style={{ color: getSourceColor(data.source) }}
                    >
                      {getSourceIcon(data.source)}
                    </div>
                    <div className="source-info">
                      <h3 className="source-name">
                        {data.source.charAt(0).toUpperCase() +
                          data.source.slice(1)}
                      </h3>
                      <span
                        className={`medium-badge ${getMediumBadgeClass(
                          data.medium
                        )}`}
                      >
                        {data.medium}
                      </span>
                    </div>
                    <div className="source-percentage">
                      {sessionPercentage}%
                    </div>
                  </div>

                  <div className="source-stats">
                    <div className="stat-row">
                      <div className="stat-item">
                        <span className="stat-label">Oturum</span>
                        <span className="stat-value">{data.sessions}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Dönüşüm</span>
                        <span className="stat-value success">
                          {data.conversions}
                        </span>
                      </div>
                    </div>
                    <div className="stat-row">
                      <div className="stat-item">
                        <span className="stat-label">Dönüşüm Oranı</span>
                        <span
                          className={`stat-value ${
                            conversionRate > 10
                              ? "success"
                              : conversionRate > 5
                              ? "warning"
                              : "danger"
                          }`}
                        >
                          {conversionRate}%
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Ort. Süre</span>
                        <span className="stat-value">
                          {formatDuration(avgDuration)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {data.campaign && data.campaign !== "none" && (
                    <div className="campaign-info">
                      <span className="campaign-label">Kampanya:</span>
                      <span className="campaign-name">{data.campaign}</span>
                    </div>
                  )}

                  <div className="source-progress">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${sessionPercentage}%`,
                        backgroundColor: getSourceColor(data.source),
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default TrafficSources;
